import React, { Children } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Lock, Shield, Eye, Database } from 'lucide-react';
export const PrivacyPage = () => {
  return <div className="min-h-screen flex flex-col bg-white" data-id="element-1801">
      <Header data-id="element-1802" />

      <main id="main-content" className="flex-grow py-12" role="main" data-id="element-1803">
        <div className="container" data-id="element-1804">
          <div className="max-w-4xl mx-auto" data-id="element-1805">
            {/* Header */}
            <div className="text-center mb-12" data-id="element-1806">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-1807">
                <Lock size={32} className="text-primary-600" data-id="element-1808" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1809">
                Privacy Policy
              </h1>
              <p className="text-lg text-gray-600" data-id="element-1810">
                Last Updated: January 15, 2025
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none" data-id="element-1811">
              <section className="mb-8" data-id="element-1812">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1813">
                  1. Introduction
                </h2>
                <p className="text-gray-700 leading-relaxed" data-id="element-1814">
                  Geldhaus Africa ("we," "us," or "our") is committed to
                  protecting your privacy. This Privacy Policy explains how we
                  collect, use, disclose, and safeguard your personal
                  information when you use our website, mobile applications, and
                  financial services.
                </p>
              </section>

              <section className="mb-8" data-id="element-1815">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1816">
                  2. Information We Collect
                </h2>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2" data-id="element-1817">
                  <Database size={20} className="text-primary-600" data-id="element-1818" />
                  Personal Information
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1819">
                  We collect the following personal information:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6" data-id="element-1820">
                  <li data-id="element-1821">Full name, date of birth, and gender</li>
                  <li data-id="element-1822">National ID or Passport number</li>
                  <li data-id="element-1823">Contact information (phone, email, physical address)</li>
                  <li data-id="element-1824">Employment and income details</li>
                  <li data-id="element-1825">Bank account and M-Pesa information</li>
                  <li data-id="element-1826">Vehicle details (for logbook loans)</li>
                  <li data-id="element-1827">Business registration documents</li>
                  <li data-id="element-1828">Credit history and financial records</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1829">
                  Automatically Collected Information
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-1830">
                  <li data-id="element-1831">IP address and device information</li>
                  <li data-id="element-1832">Browser type and operating system</li>
                  <li data-id="element-1833">Pages visited and time spent on site</li>
                  <li data-id="element-1834">Referring website and search terms</li>
                  <li data-id="element-1835">Location data (with your consent)</li>
                </ul>
              </section>

              <section className="mb-8" data-id="element-1836">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1837">
                  3. How We Use Your Information
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1838">
                  We use your personal information for the following purposes:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-1839">
                  <li data-id="element-1840">
                    <strong data-id="element-1841">Loan Processing:</strong> To evaluate your loan
                    application and creditworthiness
                  </li>
                  <li data-id="element-1842">
                    <strong data-id="element-1843">Identity Verification:</strong> To confirm your
                    identity and prevent fraud
                  </li>
                  <li data-id="element-1844">
                    <strong data-id="element-1845">Communication:</strong> To send loan updates,
                    payment reminders, and promotional offers
                  </li>
                  <li data-id="element-1846">
                    <strong data-id="element-1847">Credit Reporting:</strong> To report your credit
                    history to credit reference bureaus
                  </li>
                  <li data-id="element-1848">
                    <strong data-id="element-1849">Legal Compliance:</strong> To comply with regulatory
                    requirements and legal obligations
                  </li>
                  <li data-id="element-1850">
                    <strong data-id="element-1851">Service Improvement:</strong> To analyze usage
                    patterns and improve our services
                  </li>
                </ul>
              </section>

              <section className="mb-8" data-id="element-1852">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1853">
                  4. Information Sharing & Disclosure
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1854">
                  We may share your information with:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4" data-id="element-1855">
                  <li data-id="element-1856">
                    <strong data-id="element-1857">Credit Reference Bureaus:</strong> Metropol, CRB
                    Africa, Creditinfo
                  </li>
                  <li data-id="element-1858">
                    <strong data-id="element-1859">Regulatory Authorities:</strong> Central Bank of
                    Kenya, Kenya Revenue Authority
                  </li>
                  <li data-id="element-1860">
                    <strong data-id="element-1861">Service Providers:</strong> Payment processors, SMS
                    providers, cloud storage
                  </li>
                  <li data-id="element-1862">
                    <strong data-id="element-1863">Legal Authorities:</strong> When required by law or
                    court order
                  </li>
                  <li data-id="element-1864">
                    <strong data-id="element-1865">Business Partners:</strong> With your explicit
                    consent
                  </li>
                </ul>
                <p className="text-gray-700 leading-relaxed" data-id="element-1866">
                  We do not sell your personal information to third parties.
                </p>
              </section>

              <section className="mb-8" data-id="element-1867">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1868">
                  <Shield size={24} className="text-primary-600" data-id="element-1869" />
                  5. Data Security
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1870">
                  We implement industry-standard security measures to protect
                  your information:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-1871">
                  <li data-id="element-1872">SSL/TLS encryption for data transmission</li>
                  <li data-id="element-1873">Encrypted storage of sensitive data</li>
                  <li data-id="element-1874">Regular security audits and penetration testing</li>
                  <li data-id="element-1875">Access controls and authentication protocols</li>
                  <li data-id="element-1876">Employee training on data protection</li>
                  <li data-id="element-1877">Secure data centers with physical security</li>
                </ul>
              </section>

              <section className="mb-8" data-id="element-1878">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1879">
                  <Eye size={24} className="text-primary-600" data-id="element-1880" />
                  6. Your Rights
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1881">
                  Under Kenya's Data Protection Act, 2019, you have the right
                  to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-1882">
                  <li data-id="element-1883">
                    <strong data-id="element-1884">Access:</strong> Request a copy of your personal
                    data
                  </li>
                  <li data-id="element-1885">
                    <strong data-id="element-1886">Correction:</strong> Update inaccurate or incomplete
                    information
                  </li>
                  <li data-id="element-1887">
                    <strong data-id="element-1888">Deletion:</strong> Request deletion of your data
                    (subject to legal requirements)
                  </li>
                  <li data-id="element-1889">
                    <strong data-id="element-1890">Objection:</strong> Object to processing of your
                    data for marketing purposes
                  </li>
                  <li data-id="element-1891">
                    <strong data-id="element-1892">Portability:</strong> Receive your data in a
                    structured, machine-readable format
                  </li>
                  <li data-id="element-1893">
                    <strong data-id="element-1894">Withdraw Consent:</strong> Withdraw consent for data
                    processing at any time
                  </li>
                </ul>
              </section>

              <section className="mb-8" data-id="element-1895">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1896">
                  7. Cookies & Tracking
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1897">
                  We use cookies and similar technologies to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4" data-id="element-1898">
                  <li data-id="element-1899">Remember your preferences and settings</li>
                  <li data-id="element-1900">Analyze website traffic and usage patterns</li>
                  <li data-id="element-1901">Provide personalized content and advertisements</li>
                  <li data-id="element-1902">Improve website performance and security</li>
                </ul>
                <p className="text-gray-700 leading-relaxed" data-id="element-1903">
                  You can manage cookie preferences through our cookie banner or
                  your browser settings.
                </p>
              </section>

              <section className="mb-8" data-id="element-1904">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1905">
                  8. Data Retention
                </h2>
                <p className="text-gray-700 leading-relaxed" data-id="element-1906">
                  We retain your personal information for as long as necessary
                  to fulfill the purposes outlined in this policy, or as
                  required by law. Loan records are typically retained for 7
                  years after loan closure for regulatory compliance.
                </p>
              </section>

              <section className="mb-8" data-id="element-1907">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1908">
                  9. Children's Privacy
                </h2>
                <p className="text-gray-700 leading-relaxed" data-id="element-1909">
                  Our services are not intended for individuals under 18 years
                  of age. We do not knowingly collect personal information from
                  children.
                </p>
              </section>

              <section className="mb-8" data-id="element-1910">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1911">
                  10. International Data Transfers
                </h2>
                <p className="text-gray-700 leading-relaxed" data-id="element-1912">
                  Your data may be transferred to and processed in countries
                  outside Kenya. We ensure adequate safeguards are in place to
                  protect your information in accordance with Kenyan data
                  protection laws.
                </p>
              </section>

              <section className="mb-8" data-id="element-1913">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1914">
                  11. Changes to This Policy
                </h2>
                <p className="text-gray-700 leading-relaxed" data-id="element-1915">
                  We may update this Privacy Policy from time to time. Changes
                  will be posted on this page with an updated "Last Modified"
                  date. We encourage you to review this policy periodically.
                </p>
              </section>

              <section className="mb-8" data-id="element-1916">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1917">
                  12. Contact Us
                </h2>
                <div className="bg-gray-50 rounded-xl p-6" data-id="element-1918">
                  <p className="text-gray-700 mb-4" data-id="element-1919">
                    For privacy-related inquiries or to exercise your rights,
                    contact our Data Protection Officer:
                  </p>
                  <ul className="space-y-2 text-gray-700" data-id="element-1920">
                    <li data-id="element-1921">
                      <strong data-id="element-1922">Email:</strong> privacy@geldhaus.africa
                    </li>
                    <li data-id="element-1923">
                      <strong data-id="element-1924">Phone:</strong> +254 100 254 254
                    </li>
                    <li data-id="element-1925">
                      <strong data-id="element-1926">Address:</strong> Nairobi, Kenya
                    </li>
                  </ul>
                </div>
              </section>

              <div className="bg-primary-50 border-l-4 border-primary-600 p-6 rounded-r-lg mt-8" data-id="element-1927">
                <p className="font-semibold text-primary-900 mb-2" data-id="element-1928">
                  Office of the Data Protection Commissioner
                </p>
                <p className="text-primary-800 text-sm" data-id="element-1929">
                  If you believe your data protection rights have been violated,
                  you may file a complaint with the Office of the Data
                  Protection Commissioner of Kenya at{' '}
                  <a href="https://www.odpc.go.ke" className="underline" target="_blank" rel="noopener noreferrer" data-id="element-1930">
                    www.odpc.go.ke
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-1931" />
    </div>;
};